import { cn } from '@shared/lib';

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
}

export function Pagination({ page, pageSize, total, onChange }: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages <= 1) return null;

  const window: number[] = [];
  const range = 2;
  for (let p = Math.max(1, page - range); p <= Math.min(totalPages, page + range); p += 1) {
    window.push(p);
  }
  if (window[0]! > 1) window.unshift(1);
  if (window[window.length - 1]! < totalPages) window.push(totalPages);
  const dedup = Array.from(new Set(window)).sort((a, b) => a - b);

  const stepClass = (p: number, disabled = false) =>
    cn(
      'inline-flex h-9 min-w-[36px] items-center justify-center rounded-pill px-3 text-[14px] font-medium transition-colors',
      p === page ? 'bg-accent text-fg-inv' : 'text-muted hover:bg-elev hover:text-fg',
      disabled && 'cursor-not-allowed opacity-30 hover:bg-transparent',
    );

  return (
    <nav className="flex flex-wrap items-center justify-center gap-1 pt-4">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page === 1}
        className={stepClass(-1, page === 1)}
        aria-label="prev"
      >
        ←
      </button>
      {dedup.map((p, i, arr) => {
        const prev = arr[i - 1];
        const showGap = prev !== undefined && p - prev > 1;
        return (
          <span key={p} className="inline-flex items-center">
            {showGap && <span className="px-1.5 text-[14px] text-muted-2">…</span>}
            <button type="button" onClick={() => onChange(p)} className={stepClass(p)}>
              {p}
            </button>
          </span>
        );
      })}
      <button
        type="button"
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        className={stepClass(-1, page === totalPages)}
        aria-label="next"
      >
        →
      </button>
    </nav>
  );
}
